import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SeekBarContext } from ".";
import { useDimensions } from "../../hooks/useDimensions";
import { usePointerDown } from "../../hooks/usePointerDown";
import { usePointerMove } from "../../hooks/usePointerMove";
import { PointerState, usePointerState } from "../../hooks/usePointerState";
import { Handler } from "../../types/handler";
import { useControlBar } from "../ControlBar";
import { useRoot } from "../Root";
import { useVideoPlayerRefs } from "../VideoPlayerRefs";
import { useRange } from "../Range";
import { interpolateByRange } from "../../utils/interpolator";
import { useLayout } from "../Layout";

export interface SeekBarProps {
  seekBarPointerStateCallback?: (state: PointerState) => void;
  children?: React.ReactNode;
}

const clipHandlerWidth = 12;
const clipHandlerMargin = clipHandlerWidth / 2;

export const SeekBarProvider = ({
  children,
  seekBarPointerStateCallback,
}: SeekBarProps) => {
  const { seekBar, video } = useVideoPlayerRefs();
  const { isActive } = useControlBar();
  const { isMobile } = useLayout();

  const { clipRange, clippingEnabled } = useRoot();
  const { adjustedDuration, currentTimeOffset } = useRange();
  const wasVideoPlaying = useRef<boolean>(false);
  const { width, offset } = useDimensions(true, seekBar);

  const [lastPointerPosition, setLastPointerPosition] = useState<
    number | undefined
  >(undefined);

  const [activeHandler, setActiveHandler] = useState<Handler | undefined>(
    undefined
  );

  const [hoverHandler, setHoverHandler] = useState<Handler | undefined>(
    undefined
  );

  const pointerState = usePointerState(seekBar);

  useEffect(() => {
    if (seekBarPointerStateCallback !== undefined) {
      seekBarPointerStateCallback(pointerState);
    }
  }, [pointerState, seekBarPointerStateCallback]);

  const { isInside: isPointerInside, isDown: isPointerDown } = pointerState;

  const moveHandler = useCallback(
    (e: PointerEvent) => {
      if (video.current !== null) {
        const { currentTime } = video.current;

        const progressOffset =
          width * ((currentTime - currentTimeOffset) / adjustedDuration);

        const pointerPosition = e.x - offset;

        if (clipRange !== undefined && clippingEnabled) {
          const { start, end } = clipRange;

          const startOffset =
            width * ((start - currentTimeOffset) / adjustedDuration);
          const endOffset =
            width * ((end - currentTimeOffset) / adjustedDuration);

          if (Math.abs(startOffset - pointerPosition) <= clipHandlerMargin) {
            setHoverHandler(Handler.startClip);
            return;
          }

          if (Math.abs(endOffset - pointerPosition) <= clipHandlerMargin) {
            setHoverHandler(Handler.endClip);
            return;
          }

          if (Math.abs(progressOffset - pointerPosition) <= clipHandlerMargin) {
            setHoverHandler(Handler.progress);
            return;
          }
        }
      }

      setHoverHandler(undefined);
    },
    [
      adjustedDuration,
      clipRange,
      clippingEnabled,
      currentTimeOffset,
      offset,
      video,
      width,
    ]
  );

  usePointerMove(isPointerInside, moveHandler);

  /**
   *  POINTER UP/DOWN EVENT HANDLERS
   */
  const upHandler = useCallback(async () => {
    if (video.current !== null) {
      setActiveHandler(undefined);
      setLastPointerPosition(undefined);

      if (wasVideoPlaying.current) {
        await video.current?.play();
        wasVideoPlaying.current = false;
      }
    }
  }, [video]);

  const downHandler = useCallback(
    (e: PointerEvent) => {
      if (video.current !== null) {
        const isPlaying =
          video.current.currentTime > 0 &&
          !video.current.paused &&
          !video.current.ended &&
          video.current.readyState > video.current.HAVE_CURRENT_DATA;

        if (isPlaying) {
          wasVideoPlaying.current = true;
          video.current?.pause();
        }
      }

      const x = interpolateByRange(0, e.x - offset, width);
      setLastPointerPosition(x);

      const currentTime = (x / width) * adjustedDuration;
      const timeMargin = (clipHandlerMargin / width) * adjustedDuration;

      if (
        !isMobile &&
        clippingEnabled &&
        clipRange?.start !== undefined &&
        clipRange.start - currentTimeOffset + timeMargin >= currentTime
      ) {
        setActiveHandler(Handler.startClip);
        return;
      }

      if (
        !isMobile &&
        clippingEnabled &&
        clipRange?.end !== undefined &&
        clipRange.end - currentTimeOffset - timeMargin <= currentTime
      ) {
        setActiveHandler(Handler.endClip);
        return;
      }

      setActiveHandler(Handler.progress);
    },
    [
      adjustedDuration,
      clipRange,
      clippingEnabled,
      currentTimeOffset,
      isMobile,
      offset,
      video,
      width,
    ]
  );

  usePointerDown({
    on: isActive,
    ref: seekBar,
    callback: downHandler,
    reverseCallback: upHandler,
  });

  const value = useMemo(
    () => ({
      width,
      offset,
      isPointerDown,
      isPointerInside,
      activeHandler,
      hoverHandler,
      lastPointerPosition,
    }),
    [
      activeHandler,
      hoverHandler,
      isPointerDown,
      isPointerInside,
      lastPointerPosition,
      offset,
      width,
    ]
  );

  return (
    <SeekBarContext.Provider value={value}>{children}</SeekBarContext.Provider>
  );
};
